/**
 * Automatisch opslaan.
 *
 * Regels waar deze hook op ontworpen is:
 *
 * - nooit een verzoek per toetsaanslag: wijzigingen worden verzameld en na
 *   een korte stilte weggeschreven, met een harde bovengrens zodat een
 *   doortypende gebruiker toch regelmatig veilig is;
 * - nooit twee opslagacties tegelijk: er is er altijd hooguit één onderweg,
 *   en wat tijdens die opslag verandert gaat mee in de volgende;
 * - "Opgeslagen" verschijnt uitsluitend nadat de server het bevestigd heeft;
 * - een mislukte opslag laat het lokale werk volledig intact en probeert het
 *   opnieuw;
 * - een conflict (iemand anders was eerder) stopt het automatisch opslaan
 *   en wacht op een expliciete keuze van de gebruiker. Er wordt nooit
 *   stilzwijgend overschreven.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

import { DraftConflictError, apiErrorMessage, sitesApi } from '../api';
import {
  EditorAction,
  EditorState,
  editorReducer,
  initialEditorState,
  isDirty,
} from './editorReducer';

/** Stilte na de laatste wijziging voordat er wordt opgeslagen. */
const DEBOUNCE_MS = 900;
/** Bovengrens: ook wie blijft typen wordt zo vaak opgeslagen. */
const MAX_WAIT_MS = 4000;
/** Wachttijden voor herhaalde pogingen na een mislukte opslag. */
const RETRY_DELAYS_MS = [3000, 8000, 20000];

export interface SiteEditor {
  state: EditorState;
  dispatch: React.Dispatch<EditorAction>;
  /** Slaat nu op als er iets te bewaren valt, en wacht tot dat klaar is. */
  flush: () => Promise<void>;
  reload: () => Promise<void>;
}

export function useSiteEditor(websiteId: string): SiteEditor {
  const [state, dispatch] = useReducer(editorReducer, initialEditorState);

  // De opslaglus leest de state via een ref: zo kan hij altijd precies de
  // inhoud versturen die op dát moment lokaal is, zonder afhankelijk te zijn
  // van een verouderde closure.
  const stateRef = useRef(state);
  stateRef.current = state;

  const savingRef = useRef(false);
  const retryRef = useRef(0);
  const dirtySinceRef = useRef<number | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    dispatch({ type: 'loadStarted' });
    try {
      const draft = await sitesApi.getDraft(websiteId);
      if (!mountedRef.current) return;
      dispatch({ type: 'loaded', draft });
    } catch (error) {
      if (!mountedRef.current) return;
      dispatch({
        type: 'loadFailed',
        message: apiErrorMessage(error, 'De website kon niet worden geladen.'),
      });
    }
  }, [websiteId]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(async () => {
    const current = stateRef.current;
    if (savingRef.current) return;
    if (current.status !== 'ready') return;
    if (current.saveStatus === 'conflict') return;
    if (!isDirty(current)) return;

    const seq = current.changeSeq;
    const revision = current.revision;
    const pages = current.pages;

    savingRef.current = true;
    dispatch({ type: 'saveStarted', seq });
    try {
      const draft = await sitesApi.saveDraft(websiteId, revision, pages, current.site ?? undefined);
      retryRef.current = 0;
      dirtySinceRef.current = null;
      if (!mountedRef.current) return;
      dispatch({ type: 'saveSucceeded', seq, revision: draft.revision });
    } catch (error) {
      if (error instanceof DraftConflictError) {
        // Haal op wat er nú op de server staat, zodat de gebruiker een echte
        // keuze kan maken in plaats van een abstracte foutmelding.
        try {
          const serverDraft = await sitesApi.getDraft(websiteId);
          if (!mountedRef.current) return;
          dispatch({ type: 'saveConflict', serverDraft });
        } catch (fetchError) {
          if (!mountedRef.current) return;
          dispatch({
            type: 'saveFailed',
            message: apiErrorMessage(fetchError, 'De actuele versie kon niet worden opgehaald.'),
          });
        }
        return;
      }
      retryRef.current += 1;
      if (!mountedRef.current) return;
      const detail = apiErrorMessage(error, 'Opslaan is niet gelukt. Je wijzigingen staan nog hier.');
      if (process.env.NODE_ENV !== 'test') console.error('Sites draft save failed', error);
      dispatch({
        type: 'saveFailed',
        message: detail,
      });
    } finally {
      savingRef.current = false;
    }
  }, [websiteId]);

  // Plant de volgende opslag. Bewust één effect voor alle drie de redenen om
  // op te slaan (stilte, bovengrens, herkansing), zodat er geen tweede,
  // concurrerende timer kan ontstaan.
  useEffect(() => {
    if (state.status !== 'ready') return undefined;
    if (state.saveStatus === 'conflict') return undefined;
    if (!isDirty(state)) {
      dirtySinceRef.current = null;
      return undefined;
    }
    if (state.inFlightSeq !== null) return undefined;

    if (dirtySinceRef.current === null) dirtySinceRef.current = Date.now();

    let delay = DEBOUNCE_MS;
    if (state.saveStatus === 'error') {
      // `retryRef` telt het aantal mislukte pogingen; de eerste herkansing
      // hoort dus de eerste wachttijd te gebruiken, niet de tweede.
      const attempt = Math.max(0, retryRef.current - 1);
      delay = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];
    } else {
      const waited = Date.now() - dirtySinceRef.current;
      delay = Math.max(0, Math.min(DEBOUNCE_MS, MAX_WAIT_MS - waited));
    }

    const timer = window.setTimeout(() => {
      void save();
    }, delay);
    return () => window.clearTimeout(timer);
  }, [state, save]);

  const flush = useCallback(async () => {
    if (!isDirty(stateRef.current)) return;
    if (stateRef.current.saveStatus === 'conflict') return;
    // Wacht een eventuele lopende opslag af en schrijf daarna wat er
    // ondertussen bijgekomen is.
    for (let attempt = 0; attempt < 3; attempt += 1) {
      if (!savingRef.current) break;
      await new Promise((resolve) => window.setTimeout(resolve, 150));
    }
    await save();
  }, [save]);

  // Een browser die sluit terwijl er nog niet-opgeslagen werk is, krijgt de
  // standaardwaarschuwing. Geen eigen dialoog: die is niet betrouwbaar te
  // tonen op dit moment in de levenscyclus.
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (!isDirty(stateRef.current)) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  return useMemo(
    () => ({ state, dispatch, flush, reload: load }),
    [state, flush, load],
  );
}
